import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useI18n } from '../i18n/I18nContext';
import { PAGE_ROUTES } from '../i18n/paths';
import { SITE_URL, DEFAULT_OG_IMAGE } from '../seo/site';
import { webPageJsonLd } from '../seo/structuredData';

export { SITE_URL, DEFAULT_OG_IMAGE };

const pageMeta = {
  home: {
    title: 'Prof.in Michaela Braun | Veränderung verlangt Wahrheit',
    description:
      'Für Unternehmerinnen, Unternehmer und Führungskräfte in Verhandlungen, Konflikten und Nachfolge. Rechtsanwältin, Mediatorin und Coachin in München.',
    path: '/',
    image: `${SITE_URL}/img/michaela_about.png`,
  },
  about: {
    title: 'Über mich | Prof.in Michaela Braun',
    description:
      'Rechtsanwältin, Mediatorin und Honorarprofessorin in München. Begleitung in Verhandlungen, Konflikten und Familienunternehmen.',
    path: '/ueber-mich',
    image: `${SITE_URL}/img/michaela_ueber_mich.png`,
  },
  leistungen: {
    title: 'Drei Räume der Spiegel-Arbeit | Michaela Braun',
    description:
      '1:1 Spiegel-Arbeit, Spiegelkraft für Systeme und Spiegel für Wirkung und Haltung. Für Verhandlungen, Konflikte und Top-Management.',
    path: '/leistungen',
    image: `${SITE_URL}/img/michaela_leistungen_coaching.png`,
  },
  stimmen: {
    title: 'Stimmen | Coaching, Mediation, Workshops',
    description:
      'Rückmeldungen aus Geschäftsführung, Führungskreis und Beratung zu Coaching, Mediation und Workshops mit Prof.in Michaela Braun.',
    path: '/stimmen',
  },
  impulse: {
    title: 'Impulse zu Wirkung und Führung | Michaela Braun',
    description:
      'Fachbeiträge von Prof.in Michaela Braun: das Bild, das andere von Ihnen haben, und warum Machtspiele Frauen ausbremsen.',
    path: '/impulse',
  },
  vortraege: {
    title: 'Vorträge zu Führung und Verhandlung | Michaela Braun',
    description:
      'Vorträge zu Kommunikation, schwierigen Gesprächen, Verhandlungstechnik und Führung. Prof.in Michaela Braun, München.',
    path: '/vortraege',
    image: `${SITE_URL}/img/michaela_speaker.jpeg`,
  },
  kontakt: {
    title: 'Kontakt | Vorgespräch mit Michaela Braun',
    description:
      'Schildern Sie kurz Ihr Anliegen. Im Vorgespräch klären wir, ob Spiegel-Arbeit, Systembegleitung oder ein Vortrag passt.',
    path: '/kontakt',
  },
  impressum: {
    title: 'Impressum – Michaela Braun',
    description: 'Impressum und Anbieterkennzeichnung von michaelabraun.de.',
    path: '/impressum',
    noindex: true,
  },
  datenschutz: {
    title: 'Datenschutzhinweise – Michaela Braun',
    description:
      'Datenschutzhinweise für Klientinnen und Klienten zu Coaching, Mediation und Workshops.',
    path: '/datenschutz',
    noindex: true,
  },
};

export function getPageMeta(key) {
  return pageMeta[key] || pageMeta.home;
}

const SEO_KEY = {
  home: 'home',
  about: 'about',
  leistungen: 'services',
  stimmen: 'testimonials',
  impulse: 'insights',
  vortraege: 'speaking',
  kontakt: 'contact',
  impressum: 'legal',
  datenschutz: 'privacy',
};

const ROUTE_ID = {
  home: 'home',
  about: 'about',
  leistungen: 'services',
  stimmen: 'testimonials',
  impulse: 'insights',
  vortraege: 'speaking',
  kontakt: 'contact',
  impressum: 'legal',
  datenschutz: 'privacy',
};

function Seo({ page = 'home', jsonLd, noindex = false }) {
  const { lang, copy } = useI18n();
  const meta = getPageMeta(page);
  const seoKey = SEO_KEY[page] || 'home';
  const route = PAGE_ROUTES.find((item) => item.id === (ROUTE_ID[page] || 'home'));
  const localized = copy.seo[seoKey] || copy.seo.home;
  const path = route ? route[lang] : meta.path;
  const url = `${SITE_URL}${path}`;
  const deUrl = `${SITE_URL}${route ? route.de : meta.path}`;
  const enUrl = `${SITE_URL}${route ? route.en : '/en'}`;
  const image = meta.image || DEFAULT_OG_IMAGE;
  const indexable = !(noindex || meta.noindex);
  const robots = indexable ? 'index, follow' : 'noindex, follow';
  const imageAlt = lang === 'en' ? 'Michaela Braun' : 'Prof.in Michaela Braun';
  const pageLd = indexable
    ? webPageJsonLd({
        url,
        title: localized.title,
        description: localized.description,
        lang,
        image,
      })
    : null;

  useEffect(() => {
    document.head.querySelectorAll('[data-static-seo]').forEach((node) => node.remove());
  }, [url]);

  return (
    <Helmet prioritizeSeoTags>
      <html lang={lang} />
      <title>{localized.title}</title>
      <meta name="description" content={localized.description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="de" href={deUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={deUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:locale" content={lang === 'en' ? 'en_GB' : 'de_DE'} />
      <meta property="og:locale:alternate" content={lang === 'en' ? 'de_DE' : 'en_GB'} />
      <meta property="og:site_name" content="Michaela Braun" />
      <meta property="og:title" content={localized.title} />
      <meta property="og:description" content={localized.description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={imageAlt} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={localized.title} />
      <meta name="twitter:description" content={localized.description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {pageLd && (
        <script type="application/ld+json">
          {JSON.stringify(pageLd)}
        </script>
      )}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

export default Seo;
